/**
 * hvsin — 服务依赖解析
 *
 * 从 SSRApplication 提取的核心服务实例化逻辑。
 * 支持 Mod 内引用、跨 Mod 引用和接口式引用三种依赖解析方式。
 */
/**
 * 解析依赖引用格式
 *
 * 支持两种格式：
 * - 裸 serviceTag（如 'logger'）：Mod 内引用
 * - modName.serviceTag（如 'data.dataProvider'）：跨 Mod 引用
 *
 * 注意：以大写字母开头的引用（如 'IEmailService'）是接口引用，
 * 不经过此函数解析，由 instantiateService 直接查接口注册表。
 *
 * @param ref 依赖引用字符串
 * @returns 解析结果，包含可选的 modName 和必需的 serviceTag
 */
export function parseDependencyRef(ref) {
    const dotIndex = ref.indexOf('.');
    if (dotIndex !== -1) {
        const modName = ref.slice(0, dotIndex);
        const serviceTag = ref.slice(dotIndex + 1);
        return { modName, serviceTag };
    }
    return { serviceTag: ref };
}
/**
 * 判断是否为 class 构造器
 *
 * 方法 1: 检查是否有 prototype.constructor
 * 方法 2: 检查 toString 是否以 'class' 开头
 *
 * @param fn 要检查的函数
 * @returns 是否为 class 构造器
 */
export function isConstructor(fn) {
    if (typeof fn !== 'function')
        return false;
    // 方法 1: 检查是否有 prototype.constructor
    if (fn.prototype && fn.prototype.constructor === fn) {
        return true;
    }
    // 方法 2: 检查 toString 是否包含 'class'
    if (/^class\s/.test(fn.toString())) {
        return true;
    }
    return false;
}
/**
 * 判断依赖引用是否为接口引用
 *
 * 接口引用的特征：不含点号（非跨 Mod 引用）且以大写字母开头（约定接口名以 I 开头）
 *
 * @param ref 依赖引用字符串
 * @returns 是否为接口引用
 */
export function isInterfaceRef(ref) {
    // 含点号的是跨 Mod 引用，不是接口引用
    if (ref.includes('.'))
        return false;
    // 以大写字母开头的是接口引用（约定：接口名以 I 开头，如 IEmailService）
    return /^[A-Z]/.test(ref);
}
/**
 * 实例化服务（支持跨 Mod 依赖和接口式依赖注入）
 *
 * 解析流程：
 * 1. 遍历 dependencies 列表
 * 2. 对每个依赖，按优先级判断：
 *    a. 接口引用（如 'IEmailService'）→ 查接口注册表
 *    b. 跨 Mod 引用（如 'data.dataProvider'）→ 从目标 Mod 获取
 *    c. Mod 内引用（如 'logger'）→ 从当前 Mod 获取
 * 3. 组装依赖对象，实例化服务
 *
 * @param def 服务定义
 * @param existingServices 当前 Mod 已实例化的服务映射表
 * @param loadedMods 所有已加载 Mod 的映射表（用于跨 Mod 依赖解析）
 * @param interfaceRegistry 接口注册表（用于接口式依赖注入）
 * @returns 服务实例
 */
export function instantiateService(def, existingServices, loadedMods, interfaceRegistry) {
    const { serviceImpl, dependencies = [] } = def;
    // 解析依赖
    const depsRecord = {};
    for (const dep of dependencies) {
        // 优先检查是否为接口引用
        if (isInterfaceRef(dep) && interfaceRegistry?.has(dep)) {
            // 接口式引用：从接口注册表查找
            const entry = interfaceRegistry.get(dep);
            depsRecord[entry.serviceTag] = entry.instance;
            continue;
        }
        // 解析引用格式（Mod 内 or 跨 Mod）
        const ref = parseDependencyRef(dep);
        if (ref.modName) {
            // 跨 Mod 引用：从目标 Mod 获取服务实例
            const targetMod = loadedMods.get(ref.modName);
            if (!targetMod) {
                throw new Error(`Mod not found: ${ref.modName}`);
            }
            const targetService = targetMod.services.get(ref.serviceTag);
            if (!targetService) {
                throw new Error(`Service not found: ${ref.modName}.${ref.serviceTag}`);
            }
            depsRecord[ref.serviceTag] = targetService;
        }
        else {
            // Mod 内引用：从当前 Mod 获取
            const service = existingServices.get(ref.serviceTag);
            if (!service) {
                throw new Error(`Service not found in current mod: ${ref.serviceTag}`);
            }
            depsRecord[ref.serviceTag] = service;
        }
    }
    // 判断是 class 还是 factory function，注入依赖对象
    if (isConstructor(serviceImpl)) {
        return new serviceImpl(depsRecord);
    }
    return serviceImpl(depsRecord);
}
//# sourceMappingURL=resolver.js.map